import React, { useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { fetchUserProfileThunk } from "@/state/userProfile/userProfileSlice";
import { setToken, clearToken, setClerkId, clearClerkId } from "@/state/token/clientTokenSlice";
import { useLocation, useNavigate } from "react-router-dom";
import TermsAndConditions from "@/components/T&C/TermsAndConditions";
import { UserProfile } from "@/components/Profile/UserProfile";
import { fetchTermsMeta, fetchSubscriptionStatus } from "@/api";
import backgroundSplash from "@/Intelligenic-images/Intelligenic-background-splash.png";
import { Box } from "@mui/material";
import { returnToFromSearch, splashPath } from "./returnTo";
import { getIntelligenicToken } from "./getIntelligenicToken";

const AuthHandler: React.FC = () => {
    const { getToken, userId, isSignedIn, isLoaded, signOut } = useAuth();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const returnTo = returnToFromSearch(location.search);

    const [hasProcessed, setHasProcessed] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);

    // T&C gating
    const [tcGateChecked, setTcGateChecked] = useState(false);
    const [shouldShowTC, setShouldShowTC] = useState(false); // SET A & needs acceptance
    const [tcVersion, setTcVersion] = useState<string | null>(null);
    const [tcCompleted, setTcCompleted] = useState(false); // T&C either not required OR accepted

    // Subscription gating
    const [subscriptionChecked, setSubscriptionChecked] = useState(false);
    const [hasSubscription, setHasSubscription] = useState(false);

    // Profile completion gating
    const [shouldShowProfile, setShouldShowProfile] = useState(false);
    const [profileCompleted, setProfileCompleted] = useState(false);

    // Get user profile from Redux store
    const userProfile = useAppSelector(state => state.userProfiles.userProfile);

    // 1) Do auth / load profile
    useEffect(() => {
        const processAuth = async () => {
            if (!isLoaded || hasProcessed) return;

            if (!isSignedIn) {
                dispatch(clearToken());
                dispatch(clearClerkId());
                setHasProcessed(true);
                return;
            }

            try {
                setAuthError(null);
                const authToken = await getIntelligenicToken(getToken);
                if (!authToken || !userId) throw new Error("Failed to get authentication token");

                dispatch(setToken(authToken));
                dispatch(setClerkId(userId));

                await dispatch(fetchUserProfileThunk()).unwrap();
                setHasProcessed(true);
            } catch (err) {
                console.error("AuthHandler: Authentication failed:", err);
                dispatch(clearToken());
                dispatch(clearClerkId());
                setAuthError("We couldn't complete sign-in because the application API is unavailable. Check the local backend and try again.");
                setHasProcessed(true);
            }
        };

        processAuth();
    }, [isLoaded, isSignedIn, userId, hasProcessed, dispatch, getToken, navigate, returnTo]);

    // 2) T&C gating lives here now
    useEffect(() => {
        const checkTCGate = async () => {
            if (!isLoaded || !isSignedIn || !hasProcessed || authError || tcGateChecked) return;

            try {
                const idToken = await getIntelligenicToken(getToken);
                if (!idToken) throw new Error("Missing id token");
                const meta = await fetchTermsMeta(idToken);
                console.debug("TC meta", meta);
                setTcVersion(meta.tc_version ?? null);

                if (meta.show_terms && meta.requires_acceptance) {
                    // SET A + not accepted active version → show T&C
                    setShouldShowTC(true);
                    // tcCompleted remains false - user must accept T&C first
                } else {
                    // SET B OR already accepted → do not show T&C
                    setShouldShowTC(false);
                    setTcCompleted(true); // T&C not required, can proceed
                }
            } catch (err) {
                console.error("AuthHandler: T&C meta check failed:", err);
                console.log("Proceeding without T&C gating due to error.");
                // Fail-safe: don't block users on meta failure
                setShouldShowTC(false);
                setTcCompleted(true); // Fail-safe: proceed without T&C
            } finally {
                setTcGateChecked(true);
            }
        };

        checkTCGate();
    }, [isLoaded, isSignedIn, hasProcessed, authError, tcGateChecked, getToken]);

    // 2.5) Profile completion gating - check if user needs to complete profile
    // Only runs after T&C has been completed (either not required OR accepted by user)
    useEffect(() => {
        // Wait for auth and T&C to be completed
        if (!isLoaded || !isSignedIn || !hasProcessed || authError || !tcCompleted) return;

        // Check if user has a new profile that needs to be completed
        if (userProfile?.newprofile) {
            console.log("AuthHandler: New profile detected, showing profile completion dialog");
            setShouldShowProfile(true);
            setProfileCompleted(false);
        } else {
            console.log("AuthHandler: Profile already completed or not a new profile");
            setShouldShowProfile(false);
            setProfileCompleted(true);
        }
    }, [isLoaded, isSignedIn, hasProcessed, authError, tcCompleted, userProfile]);

    // 2.75) Subscription gating - check if user has active subscription
    // Only runs after profile has been completed
    useEffect(() => {
        const checkSubscription = async () => {
            // Wait for auth, T&C, and profile to be completed
            if (!isLoaded || !isSignedIn || !hasProcessed || authError || !tcCompleted || !profileCompleted || subscriptionChecked) return;

            try {
                const idToken = await getIntelligenicToken(getToken);
                if (!idToken) throw new Error("Missing id token");

                const subscriptionStatus = await fetchSubscriptionStatus(idToken);
                console.debug("Subscription status:", subscriptionStatus);
                setHasSubscription(subscriptionStatus.has_subscription);
            } catch (err) {
                console.error("AuthHandler: Subscription check failed:", err);
                // If we can't check subscription, assume they don't have one
                // This ensures we don't let users through without a valid check
                setHasSubscription(false);
            } finally {
                setSubscriptionChecked(true);
            }
        };

        checkSubscription();
    }, [isLoaded, isSignedIn, hasProcessed, authError, tcCompleted, profileCompleted, subscriptionChecked, getToken]);

    // 3) Redirect when ready: T&C passed, profile completed, and subscription valid
    useEffect(() => {
        if (!isLoaded || !isSignedIn || !hasProcessed || authError || !tcGateChecked || !profileCompleted || !subscriptionChecked) return;

        // If user must accept T&C, we'll render the T&C component instead of navigating.
        if (shouldShowTC) return;

        // If user must complete profile, we'll render the UserProfile component instead of navigating.
        if (shouldShowProfile) return;

        // If user doesn't have an active subscription, redirect to external plans page
        if (!hasSubscription) {
            const plansUrl = import.meta.env.VITE_PLANS_PAGE_URL || 'https://webstage.intelligenic.ai/plans/';
            window.location.href = plansUrl;
            return;
        }

        // All checks passed - return to the validated deep link, or dashboard.
        console.log("AuthHandler: All checks passed, redirecting to requested Product Studio location");
        navigate(returnTo, { replace: true });
    }, [isLoaded, isSignedIn, hasProcessed, authError, tcGateChecked, profileCompleted, subscriptionChecked, shouldShowTC, shouldShowProfile, hasSubscription, navigate, returnTo]);

    if (authError) {
        return (
            <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24, fontFamily: "Arial, sans-serif" }}>
                <div style={{ width: "min(440px, 100%)", padding: 28, border: "1px solid #e2e8f0", borderRadius: 12, background: "white", textAlign: "center" }}>
                    <h1 style={{ margin: "0 0 12px", fontSize: 22 }}>Sign-in could not be completed</h1>
                    <p role="alert" style={{ margin: "0 0 20px", color: "#475569", lineHeight: 1.5 }}>{authError}</p>
                    <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
                        <button
                            type="button"
                            onClick={() => {
                                setAuthError(null);
                                setHasProcessed(false);
                            }}
                            style={{ padding: "9px 16px", border: 0, borderRadius: 7, color: "white", background: "#2563eb", cursor: "pointer" }}
                        >
                            Retry
                        </button>
                        <button
                            type="button"
                            onClick={async () => {
                                await signOut();
                                navigate(splashPath(returnTo), { replace: true });
                            }}
                            style={{ padding: "9px 16px", border: "1px solid #cbd5e1", borderRadius: 7, color: "#334155", background: "white", cursor: "pointer" }}
                        >
                            Back to sign in
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Loading guard - wait for all initial checks to complete
    if (!isLoaded || !hasProcessed || !tcGateChecked || (tcCompleted && !profileCompleted && !shouldShowProfile) || (profileCompleted && !subscriptionChecked)) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", fontFamily: "Arial, sans-serif" }}>Loading...</div>
        );
    }

    if (!isSignedIn) return null;

    // 4) If T&C must be accepted, render T&C page
    if (shouldShowTC) {
        return (
            <div
                style={{
                    backgroundImage: `url(${backgroundSplash})`,
                    backgroundSize: "cover",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    width: "100vw",
                    fontFamily: "Arial, sans-serif"
                }}
            >
                {/* Keep TermsAndConditions as a dumb renderer (no meta call inside) */}
                <TermsAndConditions
                    onAccept={() => {
                        setShouldShowTC(false);
                        setTcCompleted(true); // <-- this triggers profile check
                    }}
                    tcVersion={tcVersion ?? undefined} // optional — purely cosmetic
                />
            </div>
        );
    }

    // 5) If profile needs to be completed, render UserProfile dialog
    if (shouldShowProfile) {
        const handleProfileClose = () => {
            console.log("AuthHandler: Profile completed, proceeding to subscription check");
            setShouldShowProfile(false);
            setProfileCompleted(true); // <-- this triggers subscription check
        };

        return (
            <div
                style={{
                    backgroundImage: `url(${backgroundSplash})`,
                    backgroundSize: "cover",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    width: "100vw",
                    fontFamily: "Arial, sans-serif"
                }}
            >
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: 1400,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "rgba(0, 0, 0, 0.5)"
                    }}
                >
                    <UserProfile
                        isOpen={shouldShowProfile}
                        onClose={handleProfileClose}
                        userName={userProfile?.username || ""}
                        isProfileUpdate={false}
                    />
                </Box>
            </div>
        );
    }

    // Otherwise, navigation effect will push to /dashboard
    return null;
};

export default AuthHandler;
